import React, { useContext, useMemo } from 'react';
import { ReactReduxContext } from 'react-redux';
import { ClockCounterClockwise, MagnifyingGlass } from '@phosphor-icons/react';
import { Link } from '../../Link';
import { Msg } from '../../../i18n-review/client/Msg';
import { useAppSelector } from '../../../redux/hooks';
import { useUserState } from '../../../query/UserProvider';
import { extractJobsFromUserState } from '../../../utils/user-state/extract-jobs-from-user-state';
import { parseSavedSearchUrlToSummary, savedSearchRelativeHref } from '../../../utils/saved-searches/parse-saved-search-summary';
import { generateJobUrl, toRelativePath, type Locale } from '../../../utils/routing/generate-url';
import { resolveJobUrlCompany, resolveJobUrlTitle } from '../../../utils/routing/job-url-input';
import { BASE_URL } from '../../../utils/constants/url-constants';
import { getRegionTagLabel } from '../../../utils/facets/region-tag-labels';
import type { Job } from '../../../types/tracked-job';
import { MenuRow } from './MenuRow';

const MAX_RECENT_SEARCHES = 3;
const MAX_VIEWED_JOBS = 2;

export interface RecentSearchChip {
  href: string;
  label: string;
  detail?: string;
}

type ChipLocale = 'en' | 'fi' | 'sv';

/**
 * Turns a stored search URL into "what you typed · where": the query first, else the first
 * location, else the first facet value. Returns null for a search with nothing readable.
 * A search saved under either location system reads correctly: the old flat `location` values
 * are already display text, the TEC-482 `regionTags` values are gazetteer codes resolved
 * through getRegionTagLabel, the same way the Searches & Alerts page shows them.
 */
export function describeRecentSearch(storedUrl: string, locale: ChipLocale = 'en'): RecentSearchChip | null {
  const summary = parseSavedSearchUrlToSummary(storedUrl);
  const regionTagLabel = summary.regionTagsValues[0] ? getRegionTagLabel(summary.regionTagsValues[0], locale) : undefined;
  const place = summary.locationValues[0] ?? regionTagLabel;
  // The first-seen threshold is stored under firstSeenMin as a code or timestamp ("24h",
  // "1700000000000"), not display text, so it never labels a chip; prefer another facet. The
  // deprecated lastSeenMin alias is excluded the same way for older saved searches.
  const facetValue = summary.facets.find((facet) => facet.attribute !== 'firstSeenMin' && facet.attribute !== 'lastSeenMin')?.values[0];
  const label = summary.query?.trim() || place || facetValue;
  if (!label) return null;
  const detail = summary.query?.trim() && place ? place : undefined;
  return { href: savedSearchRelativeHref(storedUrl), label, detail };
}

/** Newest-first, de-duplicated by target, capped. */
export function pickRecentSearchChips(
  entries: ReadonlyArray<{ url: string }>,
  locale: ChipLocale = 'en',
  limit = MAX_RECENT_SEARCHES,
): RecentSearchChip[] {
  const out: RecentSearchChip[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const chip = describeRecentSearch(entry.url, locale);
    if (!chip || seen.has(chip.href)) continue;
    seen.add(chip.href);
    out.push(chip);
    if (out.length >= limit) break;
  }
  return out;
}

/** Most recently viewed jobs that are not hidden, newest first. */
export function pickRecentlyViewedJobs(viewedJobs: Job[], limit = MAX_VIEWED_JOBS): Job[] {
  const viewedAtOf = (job: Job) => {
    const raw = (job as Record<string, unknown>).viewedAt;
    const t = typeof raw === 'string' || typeof raw === 'number' ? new Date(raw).getTime() : NaN;
    return Number.isFinite(t) ? t : 0;
  };
  return viewedJobs
    .filter((job) => !job.isHidden && (job.title || job.normalisedTitle))
    .sort((a, b) => viewedAtOf(b) - viewedAtOf(a))
    .slice(0, limit);
}

function jobHref(job: Job, locale: string): string | null {
  try {
    return toRelativePath(
      generateJobUrl({
        baseUrl: BASE_URL,
        jobId: job.id,
        locale: locale as Locale,
        title: resolveJobUrlTitle(job),
        company: resolveJobUrlCompany(job),
      }),
    );
  } catch {
    return null;
  }
}

interface ViewedRow {
  job: Job;
  href: string;
}

const ContinueBody: React.FC<{ locale: string; chips: RecentSearchChip[]; viewedRows: ViewedRow[]; onNavigate: () => void }> = ({
  locale,
  chips,
  viewedRows,
  onNavigate,
}) => {
  if (chips.length === 0 && viewedRows.length === 0) return null;
  return (
    <section aria-labelledby="menu-continue" className="space-y-2">
      <div className="flex items-center gap-2" id="menu-continue">
        <ClockCounterClockwise className="w-3.5 h-3.5 text-jc-muted" aria-hidden />
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-jc-muted whitespace-nowrap">
          <Msg
            id="navigation.menu.continue"
            defaultMessage="Pick up where you left off"
            description="Navigation menu: heading above the visitor's recent searches and recently viewed jobs"
          />
        </span>
      </div>
      {chips.length > 0 && (
        <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-5 px-5 pb-0.5">
          {chips.map((chip) => (
            <Link
              key={chip.href}
              href={chip.href}
              onClick={onNavigate}
              className="shrink-0 inline-flex items-center gap-1.5 h-9 pl-3 pr-3.5 rounded-full border border-jc-border bg-jc-surface text-sm font-medium text-jc-ink hover:bg-jc-elevated transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent"
            >
              <MagnifyingGlass className="w-3.5 h-3.5 text-jc-muted" aria-hidden />
              <span className="max-w-[11rem] truncate">{chip.label}</span>
              {chip.detail ? <span className="text-jc-muted truncate max-w-[7rem]">· {chip.detail}</span> : null}
            </Link>
          ))}
        </div>
      )}
      {viewedRows.length > 0 && (
        <div className="flex flex-col">
          {viewedRows.map(({ job, href }) => (
            <MenuRow key={job.id} href={href} locale={locale} onClick={onNavigate} compact label={resolveJobUrlTitle(job) || job.title} note={job.companyName} />
          ))}
        </div>
      )}
    </section>
  );
};

function useViewedRows(locale: string): ViewedRow[] {
  const { data: userStateData } = useUserState();
  return useMemo(
    () =>
      pickRecentlyViewedJobs(extractJobsFromUserState(userStateData ?? {}).viewedJobs)
        .map((job) => ({ job, href: jobHref(job, locale) }))
        .filter((row): row is ViewedRow => row.href !== null),
    [userStateData, locale],
  );
}

/** Variant for chrome where the Redux store (recent searches) is mounted. */
const ContinueWithSearches: React.FC<{ locale: string; onNavigate: () => void }> = ({ locale, onNavigate }) => {
  const entries = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const chips = useMemo(() => pickRecentSearchChips(entries, locale as ChipLocale), [entries, locale]);
  const viewedRows = useViewedRows(locale);
  return <ContinueBody locale={locale} chips={chips} viewedRows={viewedRows} onNavigate={onNavigate} />;
};

/** Variant for chrome without a Redux store: viewed jobs only. */
const ContinueViewedOnly: React.FC<{ locale: string; onNavigate: () => void }> = ({ locale, onNavigate }) => {
  const viewedRows = useViewedRows(locale);
  return <ContinueBody locale={locale} chips={[]} viewedRows={viewedRows} onNavigate={onNavigate} />;
};

/**
 * "Pick up where you left off": the visitor's last searches as chips and the jobs they last
 * read as rows. Recent searches live in the Redux store (localStorage-persisted), viewed jobs
 * in the TanStack user-state cache; each part renders only where its store is mounted and
 * only when it has something to show, so a first-time visitor never sees an empty section.
 */
export const MenuContinueSection: React.FC<{ locale: string; onNavigate: () => void }> = ({ locale, onNavigate }) => {
  const hasReduxStore = useContext(ReactReduxContext) != null;
  return hasReduxStore ? <ContinueWithSearches locale={locale} onNavigate={onNavigate} /> : <ContinueViewedOnly locale={locale} onNavigate={onNavigate} />;
};
